import React, { useState, useEffect } from 'react';
import { usePWA } from '../hooks/usePWA';

const PWATestPage: React.FC = () => {
  const {
    isInstallable,
    isInstalled,
    isIOS,
    isAndroid,
    isStandalone,
    canInstall,
    isOnline,
    install,
    registerServiceWorker,
    checkForUpdates,
    requestNotificationPermission,
    sendNotification
  } = usePWA();

  const [swStatus, setSwStatus] = useState<string>('Unknown');
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');

  useEffect(() => {
    // Service Worker 상태 확인
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then(registration => {
        if (registration) {
          setSwStatus('Registered');
        } else {
          setSwStatus('Not Registered');
        }
      });
    } else {
      setSwStatus('Not Supported');
    }

    // 알림 권한 상태 확인
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  const handleInstall = async () => {
    const success = await install();
    if (success) {
      alert('PWA 설치가 시작되었습니다!');
    } else {
      alert('PWA 설치에 실패했습니다.');
    }
  };

  const handleRegisterSW = async () => {
    const success = await registerServiceWorker();
    if (success) {
      setSwStatus('Registered');
      alert('Service Worker가 등록되었습니다!');
    } else {
      alert('Service Worker 등록에 실패했습니다.');
    }
  };

  const handleCheckUpdates = async () => {
    const success = await checkForUpdates();
    if (success) {
      alert('Service Worker 업데이트를 확인했습니다!');
    } else {
      alert('Service Worker 업데이트 확인에 실패했습니다.');
    }
  };

  const handleRequestNotification = async () => {
    const permission = await requestNotificationPermission();
    setNotificationPermission(permission);
    alert(`알림 권한: ${permission}`);
  };

  const handleSendNotification = () => {
    sendNotification('ScanVCF 테스트 알림', {
      body: 'PWA 기능이 정상적으로 작동합니다!',
      icon: '/icon-192x192.png'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">PWA 기능 테스트</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 디바이스 정보 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">디바이스 정보</h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>iOS:</span>
                <span className={isIOS ? 'text-green-600' : 'text-gray-500'}>
                  {isIOS ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Android:</span>
                <span className={isAndroid ? 'text-green-600' : 'text-gray-500'}>
                  {isAndroid ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Standalone:</span>
                <span className={isStandalone ? 'text-green-600' : 'text-gray-500'}>
                  {isStandalone ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Online:</span>
                <span className={isOnline ? 'text-green-600' : 'text-red-600'}>
                  {isOnline ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>

          {/* PWA 설치 상태 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">PWA 설치 상태</h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>설치 가능:</span>
                <span className={isInstallable ? 'text-green-600' : 'text-gray-500'}>
                  {isInstallable ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>설치됨:</span>
                <span className={isInstalled ? 'text-green-600' : 'text-gray-500'}>
                  {isInstalled ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>설치 가능:</span>
                <span className={canInstall ? 'text-green-600' : 'text-gray-500'}>
                  {canInstall ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
            {canInstall && (
              <button
                onClick={handleInstall}
                className="w-full mt-4 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                PWA 설치
              </button>
            )}
          </div>

          {/* Service Worker 상태 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Service Worker</h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>상태:</span>
                <span className={
                  swStatus === 'Registered' ? 'text-green-600' : 
                  swStatus === 'Not Supported' ? 'text-red-600' : 'text-yellow-600'
                }>
                  {swStatus}
                </span>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <button
                onClick={handleRegisterSW}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
              >
                Service Worker 등록
              </button>
              <button
                onClick={handleCheckUpdates}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                업데이트 확인
              </button>
            </div>
          </div>

          {/* 알림 기능 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">알림 기능</h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>권한:</span>
                <span className={
                  notificationPermission === 'granted' ? 'text-green-600' : 
                  notificationPermission === 'denied' ? 'text-red-600' : 'text-yellow-600'
                }>
                  {notificationPermission}
                </span>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <button
                onClick={handleRequestNotification}
                className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors"
              >
                알림 권한 요청
              </button>
              {notificationPermission === 'granted' && (
                <button
                  onClick={handleSendNotification}
                  className="w-full bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 transition-colors"
                >
                  테스트 알림 전송
                </button>
              )}
            </div>
          </div>
        </div>

        {/* PWA 기능 설명 */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">PWA 기능 설명</h2>
          <div className="space-y-4 text-gray-700">
            <div>
              <h3 className="font-semibold">1. Service Worker</h3>
              <p>오프라인에서도 앱을 사용할 수 있도록 캐싱 기능을 제공합니다.</p>
            </div>
            <div>
              <h3 className="font-semibold">2. PWA 설치</h3>
              <p>브라우저에서 앱을 홈 화면에 설치할 수 있습니다.</p>
            </div>
            <div>
              <h3 className="font-semibold">3. 푸시 알림</h3>
              <p>사용자에게 중요한 정보를 알림으로 전달할 수 있습니다.</p>
            </div>
            <div>
              <h3 className="font-semibold">4. 오프라인 지원</h3>
              <p>인터넷 연결이 없어도 기본 기능을 사용할 수 있습니다.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWATestPage;

